import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Logo } from "@/components/Logo";
import { ProductImage } from "@/components/ProductImage";

export default function SharedBasket() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/baskets/${code}`);
        setItems(data.items);
      } catch {
        setError("This shared basket was not found or has expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [code]);

  const addAll = () => {
    const available = items.filter((i) => i.available);
    available.forEach((i) => addItem(i, i.quantity));
    toast.success(`Added ${available.length} item(s) to your basket`);
    navigate("/cart");
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-[#F5F8F6] pb-28">
      <div className="bg-gradient-to-br from-[#095F2E] to-[#0B7A3B] px-5 pt-14 pb-8 rounded-b-[28px]">
        <Logo size={40} light />
        <h1 className="text-white text-2xl font-extrabold mt-5">Shared basket</h1>
        <p className="text-white/80 text-sm mt-1">Someone shared these market goods with you.</p>
      </div>

      <div className="px-4 pt-5">
        {loading ? (
          <p className="text-center text-[#6B8375] py-10">Loading…</p>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-[#6B8375]">{error}</p>
            <button onClick={() => navigate("/market")} className="mt-5 bg-[#0B7A3B] text-white font-bold rounded-full px-6 py-3">Go to market</button>
          </div>
        ) : (
          <div className="space-y-2.5" data-testid="shared-items">
            {items.map((it) => (
              <div key={it.product_id} className="bg-white rounded-[16px] p-3 flex items-center gap-3">
                <ProductImage src={it.image} alt={it.name} className="w-14 h-14 rounded-[12px] object-cover bg-[#F5F8F6]" iconSize={20} />
                <div className="flex-1">
                  <h3 className="font-bold text-sm text-[#122117]">{it.name}</h3>
                  <p className="text-xs text-[#6B8375]">Qty {it.quantity} · {money(it.price)}</p>
                </div>
                {!it.available && (
                  <span className="bg-gray-200 text-gray-500 text-[10px] uppercase font-bold rounded-full px-2 py-1">Sold out</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {!loading && !error && items.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-[#F5F8F6] p-4 pb-safe max-w-md mx-auto">
          <button onClick={addAll} className="w-full bg-[#0B7A3B] text-white font-extrabold rounded-full py-4 active:scale-95 transition-transform" data-testid="add-shared-to-basket">
            Add to my basket
          </button>
        </div>
      )}
    </div>
  );
}
